<script setup lang="ts">
  import { computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import tableIcon from '@/shared/assets/icons/TableIcon.svg';
  import { DonutChart, type DonutChartSegment } from '@/shared/ui';
  import { useDashboardStore } from '../../model/dashboard.store';
  import { formatNumber, formatVariationPct } from '../../model/format';
  import { formatPeriodLabel } from '../../model/period';
  import VariationBadge from '../VariationBadge.vue';
  import WidgetCard from '../WidgetCard.vue';

  interface DonutBlock {
    title: string;
    segments: DonutChartSegment[];
    centerValue: string;
    centerLabel: string;
    variationPct: number | null;
    badgeText?: string;
  }

  const dashboardStore = useDashboardStore();
  const { data, isLoading, comparisonLabel, granularity, period } = storeToRefs(dashboardStore);

  const periodLabel = computed(() => formatPeriodLabel(granularity.value, period.value));

  function buildBadgeText(variationPct: number | null, subject: string): string | undefined {
    if (variationPct === null || variationPct === 0) return undefined;
    const direction = variationPct > 0 ? 'mais' : 'menos';
    return `${formatVariationPct(variationPct)} ${direction} ${subject}`;
  }

  function buildBlock(
    title: string,
    centerLabel: string,
    badgeSubject: string,
    total: number,
    segments: DonutChartSegment[],
    variationPct: number | null
  ): DonutBlock {
    const effectiveVariationPct = total > 0 ? variationPct : null;
    return {
      title,
      centerLabel,
      segments,
      centerValue: formatNumber(total),
      variationPct: effectiveVariationPct,
      badgeText: buildBadgeText(effectiveVariationPct, badgeSubject),
    };
  }

  const blocks = computed<DonutBlock[]>(() => {
    const generatedArchived = data.value?.recordsManagement.generatedArchived;
    const matrices = data.value?.recordsManagement.matrices;
    return [
      buildBlock(
        'Registros Gerados e Arquivados',
        'Total de Registros',
        'Gerados',
        generatedArchived?.total ?? 0,
        [
          { label: 'Gerados', value: generatedArchived?.generated ?? 0 },
          { label: 'Arquivados', value: generatedArchived?.archived ?? 0 },
        ],
        generatedArchived?.variationPct ?? null
      ),
      buildBlock(
        'Matrizes Vigentes e Obsoletas',
        'Total de matrizes vigentes',
        'Matrizes Vigentes',
        matrices?.totalActive ?? 0,
        [
          { label: 'Matrizes Vigentes', value: matrices?.active ?? 0 },
          { label: 'Matrizes Obsoletas', value: matrices?.obsolete ?? 0 },
        ],
        matrices?.variationPct ?? null
      ),
    ];
  });
</script>

<template>
  <WidgetCard
    title="Gestão de Registros"
    :icon="tableIcon"
    :period-label="periodLabel"
    :loading="isLoading"
    :has-data="data !== null"
    skeleton="donuts"
  >
    <div
      data-testid="records-management-widget"
      class="flex h-full items-center gap-5"
    >
      <section
        v-for="(block, index) in blocks"
        :key="block.title"
        data-testid="records-management-block"
        class="flex min-w-0 flex-1 flex-col gap-5"
        :class="index === 0 ? 'border-r border-gray-02 py-4 pl-4 pr-5' : 'p-4'"
      >
        <h3 class="text-sm font-normal text-gray-07">
          {{ block.title }}
        </h3>
        <DonutChart
          class="self-center"
          :segments="block.segments"
          :center-value="block.centerValue"
          :center-label="block.centerLabel"
        />
        <VariationBadge
          class="self-center"
          :variation-pct="block.variationPct"
          :text="block.badgeText"
          :comparison-label="comparisonLabel"
        />
      </section>
    </div>
  </WidgetCard>
</template>
